import { cloneObj, removeDuplicates } from 'helpers/AppHelpers';
import { handleDateFilter, handleIdsFilter, handleNamesFilter } from 'helpers/ChipGenerator';

const defaultFilter = {
  originLocationIds: [],
  originLocationJobNumberIds: [],
  originSublocationIds: [],
  billOfLadingIds: [],
  picklistAssetStatusNames: [],
  trackAndTrailerAssetDrCodes: [],
  pickUpDateFrom: null,
  pickUpDateTo: null
};

const generateFilter = (id) => ({
  filters: { picklistIds: [id], ...defaultFilter },
  sortRules: [],
  responseReceived: false
});

const PICK_UP_PREFIX = '';

const generateChips = (criteria, filters = {}) => {
  const {
    locationsWithSublocations,
    sublocations,
    billOfLadings,
    picklistAssetStatuses,
    trackAndTrailerAssetDrCodes
  } = criteria;
  const truckOptions = trackAndTrailerAssetDrCodes.map((name) => ({ name, displayName: name }));

  let newChips = [];

  const filterConfigs = [
    { options: locationsWithSublocations, getLabel: (el) => el.siteCode, key: 'originLocationIds' },
    {
      options: locationsWithSublocations,
      getLabel: (el) => el.locationJobNumber,
      key: 'originLocationJobNumberIds'
    },
    { options: sublocations, getLabel: (el) => el.name, key: 'originSublocationIds' },
    { options: billOfLadings, getLabel: (el) => el.freightBill, key: 'billOfLadingIds' }
  ];

  filterConfigs.forEach(({ options, getLabel, key }) => {
    if (filters?.[key]?.length) {
      newChips = newChips.concat(handleIdsFilter(filters[key], options, getLabel, key));
    }
  });

  const { pickUpDateFrom, pickUpDateTo } = filters;

  if (filters?.picklistAssetStatusNames?.length) {
    newChips = newChips.concat(
      handleNamesFilter(
        filters.picklistAssetStatusNames,
        picklistAssetStatuses,
        'picklistAssetStatusNames'
      )
    );
  }

  if (filters?.trackAndTrailerAssetDrCodes?.length) {
    newChips = newChips.concat(
      handleNamesFilter(
        filters.trackAndTrailerAssetDrCodes,
        truckOptions,
        'trackAndTrailerAssetDrCodes'
      )
    );
  }

  if (pickUpDateFrom || pickUpDateTo) {
    newChips = newChips.concat(handleDateFilter(pickUpDateFrom, pickUpDateTo, PICK_UP_PREFIX));
  }

  return newChips.filter(Boolean);
};

const getUniqueLocationIds = (values) => {
  const originLocationIds = values?.originLocationIds || [];
  const originJobNumberIds = values?.originLocationJobNumberIds || [];
  return removeDuplicates([...originLocationIds, ...originJobNumberIds]);
};
const getMatchingSublocationOptions = (
  uniqueLocationIds,
  { sublocations, locationsWithSublocations }
) =>
  sublocations.filter(({ id }) =>
    locationsWithSublocations.some(
      ({ id: locationId, sublocations }) =>
        uniqueLocationIds.includes(locationId) &&
        sublocations.some((sublocation) => sublocation.id === id)
    )
  );
const processSublocations = (values, criteria) => {
  const uniqueLocationIds = getUniqueLocationIds(values);

  if (!uniqueLocationIds?.length) return { fieldValue: [], options: [] };

  const options = getMatchingSublocationOptions(uniqueLocationIds, criteria);

  const selectedSublocationIds = values.originSublocationIds || [];
  const fieldValue = selectedSublocationIds.filter((id) =>
    options.some((option) => option.id === id)
  );

  return { fieldValue, options };
};

const removeChip = (criteria, filters, deletedChip) => {
  const newFilters = cloneObj(filters);
  const { key, itemId, itemName } = deletedChip;

  switch (key) {
    case 'picklistAssetStatusNames':
    case 'trackAndTrailerAssetDrCodes':
      newFilters[key] = newFilters[key].filter((name) => name !== itemName);
      break;
    case `duration_${PICK_UP_PREFIX}`:
      newFilters.pickUpDateTo = null;
      newFilters.pickUpDateFrom = null;
      break;
    case `dateFrom_${PICK_UP_PREFIX}`:
      newFilters.pickUpDateFrom = null;
      break;
    case `dateTo_${PICK_UP_PREFIX}`:
      newFilters.pickUpDateTo = null;
      break;
    case 'originLocationIds':
    case 'originLocationJobNumberIds': {
      newFilters[key] = newFilters[key].filter((id) => id !== itemId);
      const { fieldValue } = processSublocations(newFilters, criteria);
      newFilters.originSublocationIds = fieldValue;
      break;
    }
    default:
      newFilters[key] = newFilters[key].filter((id) => id !== itemId);
      break;
  }

  return newFilters;
};

const parseQuery = (query) => {
  const payload = { filters: { picklistIds: query.filters.picklistIds } };

  const setFilters = (key, value) => {
    if (value?.length || value) {
      payload.filters[key] = value;
    }
  };

  const filterKeys = Object.keys(defaultFilter);

  filterKeys.forEach((key) => setFilters(key, query.filters[key]));

  if (query.sortRules?.length) {
    payload.sortRules = query.sortRules;
  }

  return payload;
};

const tabs = {
  assets: 'Assets',
  looseItems: 'LooseItems',
  assemblies: 'Assemblies'
};

const defaultTab = tabs.assets;

const TOGGLE_BUTTONS = [
  { id: 1, label: 'Assets', value: tabs.assets },
  { id: 2, label: 'Loose Items', value: tabs.looseItems },
  { id: 3, label: 'Assemblies', value: tabs.assemblies }
];

const baseConfig = [
  { label: 'Origin Location (Sublocation) [Asset]', isSortable: false },
  { label: 'Picklist Notes', isSortable: false },
  { label: 'Pick Up date', isSortable: true, parameter: 'pickupDate' },
  { label: 'Status of item', isSortable: true, parameter: 'status.name' },
  { label: 'Truck or Trailer', isSortable: true, parameter: 'truck.drCode' },
  { label: 'Freight Bill', isSortable: false },
  { label: 'Load File', isSortable: false }
];

const assetsTableConfig = [
  { label: 'DR-ID', isSortable: true, parameter: 'asset.drCode' },
  ...baseConfig
];

const looseItemsTableConfig = [
  { label: 'Loose Item (QTY)', isSortable: true, parameter: 'looseItem.name' },
  ...baseConfig
];

const assembliesTableConfig = [
  { label: 'Assembly Name', isSortable: true, parameter: 'assembledLooseItem.name' },
  ...baseConfig
];

export {
  defaultFilter,
  generateFilter,
  generateChips,
  processSublocations,
  removeChip,
  parseQuery,
  tabs,
  defaultTab,
  TOGGLE_BUTTONS,
  assetsTableConfig,
  looseItemsTableConfig,
  assembliesTableConfig
};
